import { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid'
import React, { useContext, useState } from 'react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material'
import { com } from '@eidu/form'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { PersonAdd, PersonAddOutlined } from '@mui/icons-material'
import { useSnackBar } from '../feedback/SnackBarProvider'
import inviteAuthUser from '../../api/entity/user/inviteAuthUser'
import { createAuthContext } from '../../api/authorization/AuthenticationContext'
import { requireNotUndefinedOrNull } from '../../util/require'
import EntityRepository from '../../io/EntityRepository'
import { logException } from '../../util/Logging'
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import InvitationStatus = com.eidu.sharedlib.entity.user.InvitationStatus
import invitationStatusFromJsonString = com.eidu.sharedlib.entity.user.invitationStatusFromJsonString

export type CellContent = {
  value: string
  error?: string
}

type RenderCellFunction = (
  params: GridRenderCellParams<GridValidRowModel, CellContent | string>
) => React.JSX.Element | null

const getRenderInvitationCell =
  (entityRepository?: EntityRepository): RenderCellFunction =>
  ({ row, value }) => {
    const { showSnackBar } = useSnackBar()
    const authContext = createAuthContext(useContext<IAuthContext>(AuthContext))
    const [openDialog, setOpenDialog] = useState(false)
    const [loading, setLoading] = useState(false)

    const rawInvitationStatus = typeof value === 'object' ? value.value : value

    // Parse the serialized InvitationStatus data
    let invitationStatus: InvitationStatus | null = null
    try {
      if (rawInvitationStatus) {
        invitationStatus = invitationStatusFromJsonString(rawInvitationStatus)
      }
    } catch (error) {
      logException(error)
    }

    // Get the status value from the parsed object or use the raw value as fallback

    const entityId = entityIdFromString(row.id)
    const { phoneNumberLoginIdentifier: phoneNumber, emailLoginIdentifier: email } = row

    // Check if authentication credentials are present
    const hasCredentials = phoneNumber || email

    const sendInvitation = async (event?: React.MouseEvent) => {
      if (event) {
        event.stopPropagation()
      }
      setLoading(true)
      try {
        await inviteAuthUser({ entityId, authContext: requireNotUndefinedOrNull(authContext) })

        // Refresh the entity cache with the entity we sent the invitation to
        if (entityRepository) {
          await entityRepository.refreshAll()
        }

        // Determine message based on credential type
        let message = 'Invitation sent successfully'
        if (email && !phoneNumber) {
          message = 'Invitation email sent successfully'
        } else if (phoneNumber && !email) {
          message = 'Invitation SMS sent successfully'
        }

        showSnackBar(message, 'success')
      } catch (error) {
        showSnackBar('Failed to send invitation', 'error')
        logException(error)
      } finally {
        setLoading(false)
        setOpenDialog(false)
      }
    }

    const handleInvite = async (event: React.MouseEvent) => {
      // Don't do anything if already loading
      if (loading) {
        return
      }

      // Stop propagation to prevent row click
      event.stopPropagation()

      // For re-sending invitations, show confirmation dialog
      if (invitationStatus === InvitationStatus.Success || invitationStatus === InvitationStatus.Failure) {
        setOpenDialog(true)
        return
      }

      sendInvitation(event)
    }

    const handleCloseDialog = (_event: {}, _reason: 'backdropClick' | 'escapeKeyDown') => {
      setOpenDialog(false)
    }

    const handleCancelClick = (event: React.MouseEvent) => {
      event.stopPropagation()
      setOpenDialog(false)
    }

    // Don't show button if no credentials are present
    if (!hasCredentials || invitationStatus === null) {
      return null
    }

    const color =
      invitationStatus === InvitationStatus.Success
        ? 'success'
        : invitationStatus === InvitationStatus.Failure
          ? 'error'
          : 'gray'
    const icon = invitationStatus === InvitationStatus.Pending ? <PersonAddOutlined /> : <PersonAdd />

    return (
      <>
        <IconButton color={color} onClick={(event) => handleInvite(event)} disabled={loading}>
          {loading ? <CircularProgress size={24} color="inherit" /> : icon}
        </IconButton>

        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>Re-send Invitation</DialogTitle>
          <DialogContent>
            <DialogContentText>Do you want to re-send the invitation to this user?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelClick} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={(e) => sendInvitation(e)} disabled={loading}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Re-send'}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }

export default getRenderInvitationCell
